import { AlertTriangle, FileText, Layers, TrendingDown } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatCurrency } from "@/lib/utils";

import type { RepackShrinkageItem } from "@artisancode/api-types";

interface Props {
  items: RepackShrinkageItem[];
  totalShrinkageValue: number;
}

export function RepackShrinkageSummaryCards({
  items,
  totalShrinkageValue,
}: Props) {
  const totalMaterials = items.length;
  const materialsWithShrinkage = items.filter((i) => i.shrinkageQty > 0).length;
  const totalDocuments = items.reduce((sum, i) => sum + i.documentCount, 0);

  // Average shrinkage percentage across items with shrinkage
  const avgShrinkagePct =
    materialsWithShrinkage > 0
      ? (
          items.reduce((sum, i) => sum + i.shrinkagePercentage, 0) /
          materialsWithShrinkage
        ).toFixed(1)
      : "0.0";

  return (
    <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
      <Card className="shadow-xs">
        <CardHeader className="flex flex-row items-center justify-between p-3 sm:p-6 pb-1 sm:pb-2">
          <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground line-clamp-1">
            Total Nilai Susut
          </CardTitle>
          <TrendingDown className="h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0 text-rose-500" />
        </CardHeader>
        <CardContent className="p-3 sm:p-6 pt-0 sm:pt-0">
          <div className="text-lg sm:text-2xl font-bold text-rose-600 dark:text-rose-400">
            {formatCurrency(totalShrinkageValue)}
          </div>
          <p className="mt-0.5 sm:mt-1 text-[10px] sm:text-xs text-muted-foreground line-clamp-1">
            Kerugian dari susut timbangan repack
          </p>
        </CardContent>
      </Card>

      <Card className="shadow-xs">
        <CardHeader className="flex flex-row items-center justify-between p-3 sm:p-6 pb-1 sm:pb-2">
          <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground line-clamp-1">
            Bahan Mengalami Susut
          </CardTitle>
          <AlertTriangle className="h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0 text-amber-500" />
        </CardHeader>
        <CardContent className="p-3 sm:p-6 pt-0 sm:pt-0">
          <div className="text-lg sm:text-2xl font-bold">
            {materialsWithShrinkage}{" "}
            <span className="text-xs sm:text-sm font-normal text-muted-foreground">
              / {totalMaterials}
            </span>
          </div>
          <p className="mt-0.5 sm:mt-1 text-[10px] sm:text-xs text-muted-foreground line-clamp-1">
            Bahan baku dengan selisih timbangan
          </p>
        </CardContent>
      </Card>

      <Card className="shadow-xs">
        <CardHeader className="flex flex-row items-center justify-between p-3 sm:p-6 pb-1 sm:pb-2">
          <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground line-clamp-1">
            Rata-rata Susut
          </CardTitle>
          <Layers className="h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0 text-muted-foreground" />
        </CardHeader>
        <CardContent className="p-3 sm:p-6 pt-0 sm:pt-0">
          <div className="text-lg sm:text-2xl font-bold">
            {avgShrinkagePct}%
          </div>
          <p className="mt-0.5 sm:mt-1 text-[10px] sm:text-xs text-muted-foreground line-clamp-1">
            Persentase susut per bahan
          </p>
        </CardContent>
      </Card>

      <Card className="shadow-xs">
        <CardHeader className="flex flex-row items-center justify-between p-3 sm:p-6 pb-1 sm:pb-2">
          <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground line-clamp-1">
            Dokumen Dianalisis
          </CardTitle>
          <FileText className="h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0 text-muted-foreground" />
        </CardHeader>
        <CardContent className="p-3 sm:p-6 pt-0 sm:pt-0">
          <div className="text-lg sm:text-2xl font-bold">{totalDocuments}</div>
          <p className="mt-0.5 sm:mt-1 text-[10px] sm:text-xs text-muted-foreground line-clamp-1">
            Repack selesai pada periode terpilih
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
